/**
 * Space join access rules (tier gating).
 *
 * Defaults (matching the original free-tier policy):
 * - Open-joining a `public` or `unlisted` Space requires a paid tier (`access`+).
 * - `hidden` Spaces cannot be open-joined at all — invite only.
 * - Joining any Space via an accepted invite requires only the `free` tier.
 *
 * Per-Space override:
 * - A Space admin may set `allowFreeMembers` to let free-tier identities
 *   open-join (and post in) `public`/`unlisted` Spaces. Default is `false`.
 *
 * Space *creation* is gated separately (paid-only) at the API layer and is not
 * covered here.
 */
import { SUBSCRIPTION_TIER_IDS } from '../subscriptions';
/** Default minimum tier to open-join an openly joinable (`public`/`unlisted`) Space. */
export const SPACE_OPEN_JOIN_MIN_TIER = 'access';
/** Minimum tier to join any Space via an accepted invite. */
export const SPACE_INVITE_JOIN_MIN_TIER = 'free';
export const SPACE_ACCESS_RULES = {
    joinPublic: { minTier: SPACE_OPEN_JOIN_MIN_TIER },
    joinUnlisted: { minTier: SPACE_OPEN_JOIN_MIN_TIER },
    joinViaInvite: { minTier: SPACE_INVITE_JOIN_MIN_TIER },
};
/** Returns true when `tier` is at least `minimum` in the tier hierarchy. */
export function tierMeetsMinimum(tier, minimum) {
    return SUBSCRIPTION_TIER_IDS.indexOf(tier) >= SUBSCRIPTION_TIER_IDS.indexOf(minimum);
}
/**
 * A Space can be read without joining when it is not end-to-end encrypted and
 * not hidden. Matches the API read-access rule for public Spaces and unlisted
 * non-E2EE Spaces, and the client browse gate.
 */
export function isSpaceBrowsableWithoutJoining(space) {
    return !space.e2ee && space.visibility !== 'hidden';
}
/**
 * Evaluates whether an identity may join a Space. Pure function usable by both
 * the API (enforcement) and the client (pre-flight UX).
 */
export function evaluateSpaceJoin(ctx) {
    if (ctx.viaInvite) {
        if (tierMeetsMinimum(ctx.tier, SPACE_INVITE_JOIN_MIN_TIER)) {
            return { allowed: true };
        }
        return { allowed: false, reason: 'tier_required', minTier: SPACE_INVITE_JOIN_MIN_TIER };
    }
    // Open (non-invite) join.
    if (ctx.visibility === 'hidden') {
        return { allowed: false, reason: 'invite_required' };
    }
    if (ctx.allowFreeMembers) {
        return { allowed: true };
    }
    if (tierMeetsMinimum(ctx.tier, SPACE_OPEN_JOIN_MIN_TIER)) {
        return { allowed: true };
    }
    return { allowed: false, reason: 'tier_required', minTier: SPACE_OPEN_JOIN_MIN_TIER };
}
//# sourceMappingURL=space-access-rules.js.map